<template>
  <div :class="[$options.name, statusClassList]" :data-e-ga-id="uniqueId">
    <div :class="[$options.name] + 'container'">
      <div :class="[$options.name] + '__container-range'" @click="gtagUniversalSend({ component_id: uniqueId })">
        <span
          v-if="withThumb"
          :class="[$options.name + '__thumb']"
          :style="{ left: `${pixelValue}px` }"
          :data-value="internalValue"
        ></span>
        <input
          ref="range"
          :class="[$options.name + '__range']"
          type="range"
          :value="internalValue"
          :min="minValue"
          :max="props.max"
          :step="props.step"
          @input="handleInput"
        />
        <span v-if="props.unit" :class="[$options.name + '__unit']">{{ props.unit }}</span>
      </div>
    </div>
    <div v-if="props.helper" :class="[$options.name + '__helper']">{{ props.helper }}</div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, Ref, watch } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneSliderInput",
  props: {
    /**
     * Value of slider input (shown in the middle of the slider)
     */
    value: {
      type: Number,
      default: 0,
    },
    /**
     * Minimal value of slider input
     */
    min: {
      type: Number,
      default: 0,
    },
    /**
     * Maximal value of slider input
     */
    max: {
      type: Number,
      default: 100,
    },
    /**
     * Step of slider input, how much it changes when user slides it
     */
    step: {
      type: Number,
      default: 1,
    },
    /**
     * Whether the type slider input is disabled or not (greyed out slider and not clickable)
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Text shown to the right of the slider input
     */
    unit: {
      type: String,
      default: "",
    },
    /**
     * Text shown below the slider input
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * Whether the type slider input is invalid (red border around slider)
     */
    invalid: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      default: "",
    },
    withThumb: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    /**
     * Triggers at every value change (slider move)
     */
    "updateValue",
  ],
  setup(props, { emit }) {
    const internalValue = ref(props.value || props.min);
    const range: Ref<HTMLInputElement> = ref(null) as any;
    const pixelValue = ref(13);
    const minValue = ref(props.min);

    watch(
      () => props.value,
      (value) => {
        if (value >= props.max) {
          internalValue.value = props.max;
        } else if (value <= props.min) {
          internalValue.value = props.min;
        } else {
          internalValue.value = value;
        }
        calculatePixelValue();
        emit("updateValue", value);
      }
    );

    watch(
      () => props.min,
      (value) => {
        minValue.value = value;
        if (internalValue.value < value) {
          internalValue.value = value;
        }
        calculatePixelValue();
      }
    );

    const statusClassList = computed(() => {
      return {
        "EdroneSliderInput--default": !props.disabled && !props.invalid,
        "EdroneSliderInput--invalid": props.invalid && !props.disabled,
        "EdroneSliderInput--disabled": props.disabled,
      };
    });

    const handleInput = (event: Event) => {
      if (!range.value) return;
      const target = event.target as HTMLInputElement;
      internalValue.value = Number(target.value);
      if (internalValue.value != 0) {
        calculatePixelValue();
      } else {
        pixelValue.value = 13;
        range.value.style.backgroundSize = "0% 100%";
      }
      emit("updateValue", internalValue.value);
    };

    function calculatePixelValue() {
      const thumbWidth = 36;
      const width = range.value.clientWidth;
      const valueRange = props.max - minValue.value;
      pixelValue.value = ((internalValue.value - minValue.value) / valueRange) * (width - thumbWidth) + 13;
      range.value.style.backgroundSize =
        ((internalValue.value - minValue.value) * 100) / (props.max - minValue.value) + "% 100%";
    }

    onMounted(() => {
      calculatePixelValue();
    });

    return {
      internalValue,
      handleInput,
      props,
      range,
      minValue,
      pixelValue,
      statusClassList,
    };
  },
  methods: { gtagUniversalSend },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneSliderInput {
  width: 100%;
  font-family: "Roboto", sans-serif;
}

.EdroneSliderInput.EdroneSliderInput--disabled {
  pointer-events: none;
}

.EdroneSliderInput.EdroneSliderInput--disabled .EdroneSliderInput__container-range {
  @apply tw:bg-default-100 tw:border-default-100;
}

.EdroneSliderInput.EdroneSliderInput--disabled .EdroneSliderInput__range {
  @apply tw:bg-gradient-to-l tw:from-default-500 tw:to-default-500;
}

.EdroneSliderInput.EdroneSliderInput--disabled .EdroneSliderInput__range::-webkit-slider-thumb {
  @apply tw:bg-default-500;
}

.EdroneSliderInput.EdroneSliderInput--invalid .EdroneSliderInput__container-range {
  @apply tw:border tw:border-red-500;
}

.EdroneSliderInput__container-range {
  position: relative;
  width: 100%;
  white-space: nowrap;
  display: flex;
  align-items: center;
  @apply tw:border tw:border-default-200 tw:rounded-sm tw:p-3 tw:h-10;
}

.EdroneSliderInput__container-range:hover {
  @apply tw:border tw:border-default-400;
}

.EdroneSliderInput__thumb {
  position: absolute;
  left: 0;
  text-align: center;
  pointer-events: none;
  @apply tw:top-1 tw:w-9 tw:h-[1.875rem] tw:leading-[1.875rem] tw:text-xs tw:text-white;
}

.EdroneSliderInput__thumb::before {
  content: attr(data-value);
}

.EdroneSliderInput__range {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  outline: none;
  background-size: 0% 100%;
  background-repeat: no-repeat;
  width: 100%;
  @apply tw:bg-default-100 tw:border tw:border-default-100 tw:h-2 tw:rounded;
  @apply tw:bg-gradient-to-r tw:from-default-900 tw:to-default-900;
}

.EdroneSliderInput__range::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: grab;
  @apply tw:h-5 tw:w-9 tw:rounded-lg tw:bg-default-900;
}

.EdroneSliderInput__container {
  display: flex;
  align-items: center;
  @apply tw:w-48;
}

.EdroneSliderInput__unit {
  @apply tw:text-xs tw:ml-2 tw:text-default-700;
}

.EdroneSliderInput__helper {
  letter-spacing: -0.24px;
  text-overflow: ellipsis;
  max-width: 100%;
  overflow: hidden;
  @apply tw:text-xs tw:text-default-700 tw:leading-4 tw:mt-1;
}
</style>
